import { useEffect, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { CreateSellSheet } from "@/components/sheet/CreateSellSheet"
import { useDashboardStore } from "@/stores/dashboard-store"
import { fetchProductQuantities } from "@/services/products"
import { fetchOrders } from "@/services/orders"
import type { ProductQuantity } from "@/types/products"
import type { Order } from "@/types/orders"
import {
  Users,
  Package,
  Wallet,
  ShoppingCart,
  TrendingUp,
  AlertCircle,
  Building2,
  Layers,
} from "lucide-react"

function StatCard({
  title,
  value,
  description,
  icon: Icon,
}: {
  title: string
  value: string | number
  description?: string
  icon: React.ElementType
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="text-muted-foreground h-4 w-4" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-muted-foreground text-xs">{description}</p>
        )}
      </CardContent>
    </Card>
  )
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
              <Skeleton className="mt-1 h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { t, i18n } = useTranslation()
  const { data, isLoading, error, fetch } = useDashboardStore()

  // Products state
  const [quantities, setQuantities] = useState<ProductQuantity[]>([])
  const [isLoadingQuantities, setIsLoadingQuantities] = useState(false)

  // Sell sheet state
  const [isSellSheetOpen, setIsSellSheetOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<ProductQuantity | null>(null)

  // Orders state
  const [orders, setOrders] = useState<Order[]>([])
  const [isLoadingOrders, setIsLoadingOrders] = useState(false)

  useEffect(() => {
    fetch()
  }, [fetch])

  useEffect(() => {
    const loadQuantities = async () => {
      try {
        setIsLoadingQuantities(true)
        const data = await fetchProductQuantities()
        setQuantities(data)
      } catch {
        // Silently fail - dashboard will just not show products
      } finally {
        setIsLoadingQuantities(false)
      }
    }
    loadQuantities()
  }, [])

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setIsLoadingOrders(true)
        const data = await fetchOrders()
        setOrders(data)
      } catch {
        // Silently fail - dashboard will just not show orders
      } finally {
        setIsLoadingOrders(false)
      }
    }
    loadOrders()
  }, [])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("uz-UZ").format(num)
  }

  const getMonthName = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'uz-UZ', {
      month: 'long',
      year: 'numeric',
    })
  }

  // Sell handlers
  const handleOpenSell = (product: ProductQuantity) => {
    setSelectedProduct(product)
    setIsSellSheetOpen(true)
  }

  const handleSellSuccess = async () => {
    const data = await fetchProductQuantities()
    setQuantities(data)
  }

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('dashboard.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className="mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {isLoading ? (
        <LoadingSkeleton />
      ) : data ? (
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          {/* Inventory Grid: 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Products Inventory - spans 2 rows on left */}
            <Card className="lg:col-span-2 lg:row-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  {t('products.inventory')}
                </CardTitle>
                <CardDescription>{t('dashboard.currentInventory')}</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingQuantities ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-8 w-full" />
                    ))}
                  </div>
                ) : quantities.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-8 text-xs">{t('products.thickness')}</TableHead>
                        <TableHead className="h-8 text-xs">{t('products.cell')}</TableHead>
                        <TableHead className="h-8 text-xs">{t('products.height')}</TableHead>
                        <TableHead className="h-8 text-xs">{t('products.width')}</TableHead>
                        <TableHead className="h-8 text-xs">{t('products.color')}</TableHead>
                        <TableHead className="h-8 text-xs">{t('products.type')}</TableHead>
                        <TableHead className="h-8 text-xs text-right">{t('products.count')}</TableHead>
                        <TableHead className="h-8 w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quantities.map((item, index) => (
                        <TableRow key={`${item.thickness}-${item.cell}-${item.height}-${item.width}-${index}`}>
                          <TableCell className="py-2 text-sm">{item.thickness}</TableCell>
                          <TableCell className="py-2 text-sm">{item.cell}</TableCell>
                          <TableCell className="py-2 text-sm">{item.height}</TableCell>
                          <TableCell className="py-2 text-sm">{item.width}</TableCell>
                          <TableCell className="py-2 text-sm">{item.color__name}</TableCell>
                          <TableCell className="py-2 text-sm">{item.type__name}</TableCell>
                          <TableCell className="py-2 text-sm text-right font-medium">{item.total_count}</TableCell>
                          <TableCell className="py-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => handleOpenSell(item)}
                              title={t('sell.createSale')}
                            >
                              <ShoppingCart className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {t('products.noProductsFound')}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Materials Stock */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  {t('dashboard.materialsStock')}
                </CardTitle>
                <CardDescription>{t('dashboard.currentInventory')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.materials.length > 0 ? (
                    data.materials.map((material) => (
                      <div
                        key={material.id}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm font-medium">
                          {material.name}
                        </span>
                        <span className="text-muted-foreground text-sm">
                          {formatNumber(material.weight)} kg
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {t('dashboard.noMaterialsInStock')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Orders Inventory */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4" />
                  {t('orders.title')}
                </CardTitle>
                <CardDescription>{t('dashboard.currentInventory')}</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingOrders ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-8 w-full" />
                    ))}
                  </div>
                ) : orders.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-8 text-xs">ID</TableHead>
                        <TableHead className="h-8 text-xs">{t('orders.client')}</TableHead>
                        <TableHead className="h-8 text-xs">{t('orders.quantity')}</TableHead>
                        <TableHead className="h-8 text-xs">{t('common.status')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.slice(0, 8).map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="py-2 text-sm">{order.id}</TableCell>
                          <TableCell className="py-2 text-sm truncate max-w-[100px]">{order.client?.full_name || order.client?.phone_number || '-'}</TableCell>
                          <TableCell className="py-2 text-sm">{formatNumber(order.quantity)}</TableCell>
                          <TableCell className="py-2 text-sm">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              order.order_status === 'new' ? 'bg-blue-100 text-blue-800' :
                              order.order_status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                              order.order_status === 'completed' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {order.order_status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {t('orders.noOrdersFound')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stats Overview */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title={t('dashboard.activeWorkers')}
              value={data.workers.active}
              description={`${data.workers.pending} ${t('dashboard.pending')}, ${data.workers.inactive} ${t('dashboard.inactive')}`}
              icon={Users}
            />
            <StatCard
              title={t('dashboard.newOrders')}
              value={data.orders.new}
              description={`${data.orders.in_progress} ${t('dashboard.inProgress')}, ${data.orders.completed} ${t('dashboard.completed')}`}
              icon={ShoppingCart}
            />
            <StatCard
              title={t('dashboard.totalCustomerDebt')}
              value={formatCurrency(data.customers.total_debt)}
              description={`${t('dashboard.totalGiven')}: ${formatCurrency(data.customers.total_given)}`}
              icon={Wallet}
            />
            <StatCard
              title={t('dashboard.productsThisWeek')}
              value={data.products_stats.data.reduce(
                (sum, item) => sum + item.count,
                0
              )}
              description={`${t('dashboard.period')}: ${data.products_stats.period}`}
              icon={Package}
            />
          </div>

          {/* Secondary Stats */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Worker Ranking */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  {t('dashboard.workerRanking')}
                </CardTitle>
                <CardDescription>{t('dashboard.byTotalProducts')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.worker_ranking.length > 0 ? (
                    data.worker_ranking.map((worker, index) => (
                      <div
                        key={worker.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <span className="bg-muted flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium">
                            {index + 1}
                          </span>
                          <span className="text-sm font-medium">
                            {worker.full_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">
                            {formatNumber(worker.completed_orders)} {t('dashboard.orders')}
                          </span>
                          <span>
                            {formatNumber(worker.completed_products)} {t('dashboard.products')}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {t('dashboard.noWorkerData')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top Debtors */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  {t('dashboard.topDebtors')}
                </CardTitle>
                <CardDescription>
                  {t('dashboard.totalAmount')}: {formatCurrency(data.customers.total_debt)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.customers.top_debtors.length > 0 ? (
                    data.customers.top_debtors.map((debtor) => (
                      <div
                        key={debtor.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {debtor.full_name}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {debtor.phone_number}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                          {formatCurrency(debtor.debt)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {t('dashboard.noDebtorsFound')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Budget Overview, Orders Overview and Monthly Expenses */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Budget Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {t('dashboard.budgetOverview')}
                </CardTitle>
                <CardDescription>{t('dashboard.companyFinances')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('dashboard.income')}</span>
                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(data.budget.income)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('dashboard.materialsValue')}</span>
                    <span className="text-sm font-semibold">
                      {formatCurrency(data.budget.inventory.materials)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('dashboard.productsValue')}</span>
                    <span className="text-sm font-semibold">
                      {formatCurrency(data.budget.inventory.products)}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('dashboard.totalCompanyValue')}</span>
                    <span className="text-lg font-bold text-primary">
                      {formatCurrency(data.budget.total_company_value)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Orders Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4" />
                  {t('dashboard.ordersOverview')}
                </CardTitle>
                <CardDescription>{t('dashboard.currentOrderStatus')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {data.orders.new}
                    </div>
                    <div className="text-muted-foreground text-xs">{t('dashboard.new')}</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                      {data.orders.in_progress}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t('dashboard.inProgress')}
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {data.orders.completed}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t('dashboard.completed')}
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {data.orders.cancelled}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t('dashboard.cancelled')}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Expenses */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  {t('dashboard.monthlyExpenses')}
                </CardTitle>
                <CardDescription>{t('dashboard.expenseSummary')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.expenses_monthly.length > 0 ? (
                    data.expenses_monthly.map((expense) => {
                      return (
                        <div
                          key={expense.month}
                          className="flex items-center justify-between"
                        >
                          <span className="text-sm font-medium">
                            {getMonthName(expense.month)}
                          </span>
                          <span className="text-sm font-semibold">
                            {formatCurrency(expense.total)}
                          </span>
                        </div>
                      )
                    })
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {t('dashboard.noExpenseData')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : null}

      {/* Sell Sheet */}
      <CreateSellSheet
        open={isSellSheetOpen}
        onOpenChange={(open) => {
          setIsSellSheetOpen(open)
          if (!open) setSelectedProduct(null)
        }}
        selectedProduct={selectedProduct}
        onSuccess={handleSellSuccess}
        showCustomerPicker
        showSellingTypePicker
      />
    </>
  )
}
